import type { RecordsFiltersDTO } from "@/application/records/dtos/RecordListItemDTO";
import type { RecordId } from "@/domain/records/types";
import { handleUnauthorizedResponse } from "@/infrastructure/auth/authUtils";
import { createIdempotencyKey } from "../../http/request-id";
import { httpClient, type TransportRequestConfig } from "../../http/HttpClient";

export interface RecordListItemResponse {
  id: string;
  name: string;
  status: string;
  project?: string | null;
  file_extension?: string | null;
  file_name?: string | null;
  file_path?: string | null;
  file_type?: string | null;
  participants_count?: number | null;
  participant_count?: number | null;
  participants?: number | null;
  creation_date?: string | null;
  modified_date?: string | null;
  content?: string | null;
  size_mb?: number | null;
  is_archived?: boolean | null;
  is_public?: boolean | null;
  duration_sec?: number | null;
  type?: string | null;
  [key: string]: unknown;
}

export interface AdminRecordListItemResponse extends RecordListItemResponse {
  email?: string | null;
}

export interface RecordsListApiResponse<
  TRecord extends RecordListItemResponse
> {
  records?: TRecord[];
  results?: TRecord[];
  total?: number;
  remaining_size_mb?: number;
  page?: number;
  limit?: number;
  has_more?: boolean;
  next?: string | null;
  filters_applied?: Record<string, unknown> | null;
}

export type RecordUpdateChanges = Record<string, unknown>;
export type RecordUpdateResponse = Record<string, unknown> | undefined;

/**
 * Adapter for Records API integration
 * Clean Architecture - Infrastructure Layer
 */
export interface IRecordApiAdapter {
  getRecordsList(
    page?: number,
    limit?: number,
    filters?: RecordsFiltersDTO
  ): Promise<RecordsListApiResponse<RecordListItemResponse>>;
  getAdminRecordsList(
    page?: number,
    limit?: number,
    filters?: RecordsFiltersDTO
  ): Promise<RecordsListApiResponse<AdminRecordListItemResponse>>;
  updateRecords(
    updates: Array<{ id: RecordId; changes: RecordUpdateChanges }>
  ): Promise<RecordUpdateResponse>;
  archiveRecord(recordId: RecordId): Promise<void>;
  unarchiveRecord(recordId: RecordId): Promise<void>;
}

type QueryValue =
  | string
  | number
  | boolean
  | Array<string | number | boolean>
  | null
  | undefined;

type EndpointCandidate = {
  path: string;
  query?: Record<string, QueryValue>;
};

/**
 * Record API Adapter implementation
 * Handles communication with Records API endpoints
 */
export class RecordApiAdapter implements IRecordApiAdapter {
  constructor(
    private readonly baseUrl: string,
    private readonly authHeadersProvider?: () => Promise<Record<string, string>>
  ) {}

  async getRecordsList(
    page: number = 1,
    limit: number = 50,
    filters?: RecordsFiltersDTO
  ): Promise<RecordsListApiResponse<RecordListItemResponse>> {
    const query: Record<string, QueryValue> = { page, limit };
    this.appendFilters(query, filters, { includeEmail: false });

    const response = await this.fetchFromCandidates<
      RecordsListApiResponse<RecordListItemResponse>
    >([
      {
        path: "insight/",
        query,
      },
      {
        path: "insight/paginated",
        query,
      },
    ]);

    return response ?? { records: [] };
  }

  async getAdminRecordsList(
    page: number = 1,
    limit: number = 100,
    filters?: RecordsFiltersDTO
  ): Promise<RecordsListApiResponse<AdminRecordListItemResponse>> {
    const query: Record<string, QueryValue> = { page, limit };
    this.appendFilters(query, filters, { includeEmail: true });

    const response = await this.fetchFromCandidates<
      RecordsListApiResponse<AdminRecordListItemResponse>
    >([
      {
        path: "admin/records/",
        query,
      },
    ]);

    return response ?? { records: [] };
  }

  async updateRecords(
    updates: Array<{ id: RecordId; changes: RecordUpdateChanges }>
  ): Promise<RecordUpdateResponse> {
    const url = `${this.baseUrl}/records/update/`;

    const payload = {
      updates: updates.map((update) => ({
        id: update.id,
        ...update.changes,
      })),
    };

    const response = await this.makeRequest<RecordUpdateResponse>(url, {
      method: "PUT",
      body: JSON.stringify(payload),
    }, {
      idempotencyKey: createIdempotencyKey("records-update", updates.length),
    });

    return response ?? undefined;
  }

  async archiveRecord(recordId: RecordId): Promise<void> {
    const url = `${this.baseUrl}/records/${recordId}/archive/`;

    await this.makeRequest<undefined>(url, {
      method: "POST",
    }, {
      idempotencyKey: createIdempotencyKey("record-archive", recordId),
    });
  }

  async unarchiveRecord(recordId: RecordId): Promise<void> {
    const url = `${this.baseUrl}/records/${recordId}/unarchive/`;

    await this.makeRequest<undefined>(url, {
      method: "POST",
    }, {
      idempotencyKey: createIdempotencyKey("record-unarchive", recordId),
    });
  }

  /**
   * Generic request method with error handling
   */
  private async makeRequest<TResponse = unknown>(
    url: string,
    options: RequestInit = {},
    transportConfig: TransportRequestConfig = {}
  ): Promise<TResponse | null> {
    try {
      // Use HttpClient which handles automatic token refresh
      const response = await httpClient.request(url, options, transportConfig);

      if (!response.ok) {
        if (response.status === 401) {
          handleUnauthorizedResponse();
          return null;
        }

        if (response.status === 404) {
          console.warn("RecordApiAdapter received 404 for", url);
          return null;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      if (response.status === 204) {
        return null;
      }

      const raw = await response.text();
      if (!raw) {
        return null;
      }

      try {
        return JSON.parse(raw) as TResponse;
      } catch {
        return raw as unknown as TResponse;
      }
    } catch (error) {
      console.error("RecordApiAdapter request failed:", error);
      throw new Error(
        `Records API request failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  }

  private appendFilters(
    query: Record<string, QueryValue>,
    filters: RecordsFiltersDTO | undefined,
    options: { includeEmail?: boolean } = {}
  ): void {
    if (!filters) {
      return;
    }

    const trimmedSearch = filters.search?.trim();
    if (trimmedSearch) {
      query.search = trimmedSearch;
    }

    if (filters.status && filters.status.length > 0) {
      const statuses = filters.status.filter((value) => Boolean(value));
      if (statuses.length > 0) {
        query.status = statuses;
      }
    }

    if (filters.type && filters.type.length > 0) {
      const normalizedTypes = filters.type
        .map((value) => value?.toLowerCase())
        .filter((value): value is string => Boolean(value));
      if (normalizedTypes.length > 0) {
        query.type = normalizedTypes;
      }
    }

    if (filters.archived !== undefined) {
      query.archived = filters.archived;
    }

    if (options.includeEmail && filters.email) {
      query.email = filters.email;
    }
  }

  private buildUrl(path: string, query?: Record<string, QueryValue>): string {
    const base = this.baseUrl.endsWith("/") ? this.baseUrl : `${this.baseUrl}/`;
    const sanitizedPath = path.replace(/^\/+/, "");
    const url = new URL(sanitizedPath || "", base);

    if (query) {
      for (const [key, value] of Object.entries(query)) {
        if (value === undefined || value === null) {
          continue;
        }
        if (Array.isArray(value)) {
          value
            .filter((item) => item !== undefined && item !== null)
            .forEach((item) => {
              url.searchParams.append(key, String(item));
            });
          continue;
        }
        url.searchParams.set(key, String(value));
      }
    }

    const result = url.toString();
    return result;
  }

  private async fetchFromCandidates<TResponse>(
    candidates: EndpointCandidate[]
  ): Promise<TResponse | null> {
    const attemptedUrls: string[] = [];

    for (const candidate of candidates) {
      const url = this.buildUrl(candidate.path, candidate.query);
      attemptedUrls.push(url);

      const response = await this.makeRequest<TResponse>(url);
      if (response !== null && response !== undefined) {
        return response;
      }
    }

    throw new Error(
      `Records API: all candidate endpoints returned empty response. Tried: ${attemptedUrls.join(
        ", "
      )}`
    );
  }

  // buildHeaders method removed - HttpClient handles headers automatically
}
